/**
 * Состояние формы задачи. Начальные значения (buildInitialValues),
 * live-валидация и синхронизация при подгрузке existing. Набор полей
 * формы выводится из ENTITY_FIELDS_META.task (formField переименовывает
 * tags → tagsInput); status и isSummary добавляются отдельно: первое —
 * поле формы, не шаблона (Q3), второе — вычисляемое.
 */
import { useCallback, useEffect, useRef } from "react";
import { useForm } from "./useForm";
import { iso, addDays, uid, TODAY } from "../utils/date";
import { hoursBetween } from "../utils/hourlyTask";
import { parseTagsInput } from "../utils/tags";
import { ENTITY_FIELDS_META } from "../utils/templateSchemas";

const stripNotes = (task) => {
  if (!task) return task;
  const rest = { ...task };
  delete rest.notes;
  return rest;
};

const FORM_FIELDS = Object.freeze([
  ...Object.keys(ENTITY_FIELDS_META.task).map(
    (f) => ENTITY_FIELDS_META.task[f].formField ?? f,
  ),
  "status",
  "isSummary",
]);

export const normalizeFormStatus = (entity, workingStatus) => {
  if (!entity) return workingStatus;
  if (entity.status === "draft" && !entity.pendingApprovalId)
    return workingStatus;
  return entity.status;
};

function buildInitialValues({
  existing,
  isCopy,
  copySource,
  effectiveProjectId,
  parentTaskId,
  parentTask,
  ur,
}) {
  if (existing) {
    return {
      ...stripNotes(existing),
      status: normalizeFormStatus(existing, "new"),
      tagsInput: Array.isArray(existing.tags) ? existing.tags.join(", ") : "",
      plannedHours: existing.plannedHours,
      isHourly: existing.isHourly ?? false,
      startTime: existing.startTime || "09:00",
      endTime: existing.endTime || "18:00",
    };
  }

  if (isCopy) {
    return {
      ...stripNotes(copySource),
      id: "t_" + uid(),
      assigneeId: null,
      status: "new",
      parentTaskId: null,
      isSummary: false,
      tagsInput: Array.isArray(copySource.tags)
        ? copySource.tags.join(", ")
        : "",
      isHourly: copySource.isHourly ?? false,
      startTime: copySource.startTime || "09:00",
      endTime: copySource.endTime || "18:00",
      logs: [],
      history: [],
      delegatedFrom: null,
      archived: false,
      archivedAt: null,
      closedAt: null,
      creatorId: ur.id,
      dependencyId: null,
      files: [],
      folders: [],
    };
  }

  const inheritedStart = parentTask?.start ?? TODAY;
  const inheritedDeadline =
    parentTask?.deadline ?? iso(addDays(new Date(), 14));
  const inheritedProjectId = effectiveProjectId || parentTask?.projectId || "";

  return {
    id: "t_" + uid(),
    title: "",
    desc: "",
    tagsInput: "",
    projectId: inheritedProjectId,
    assigneeId: null,
    priority: "mid",
    plannedHours: 8,
    start: inheritedStart,
    deadline: inheritedDeadline,
    status: "new",
    isHourly: false,
    startTime: "09:00",
    endTime: "18:00",
    logs: [],
    history: [],
    delegatedFrom: null,
    archived: false,
    archivedAt: null,
    closedAt: null,
    creatorId: ur.id,
    dependencyId: null,
    dependencyType: "FS",
    files: [],
    folders: [],
    isSummary: false,
    parentTaskId: parentTaskId || null,
  };
}

export function useTaskFormState({
  existing,
  isCopy,
  copySource,
  isProjectLocked,
  effectiveProjectId,
  parentTaskId,
  db,
  ur,
}) {
  const parentTask = parentTaskId
    ? db.tasks.find((t) => t.id === parentTaskId) || null
    : null;

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    touched,
    setValues,
    setTouched,
    setFieldValue,
    isValid,
    isDirty,
  } = useForm(
    buildInitialValues({
      existing,
      isCopy,
      copySource,
      effectiveProjectId,
      parentTaskId,
      parentTask,
      ur,
    }),
    useCallback(
      (values) => {
        const errors = {};
        if (!values.title?.trim()) errors.title = "Название обязательно";

        if (isProjectLocked) {
          if (!values.projectId) errors.projectId = "Проект не определён";
        } else if (!values.projectId || values.projectId === "") {
          errors.projectId = "Выберите проект";
        }

        if (!values.assigneeId || values.assigneeId === "") {
          errors.assigneeId = "Выберите исполнителя";
        }

        if (!values.start) errors.start = "Дата начала обязательна";

        const project = db.projects.find((p) => p.id === values.projectId);
        if (!(project && project.ptype === "admin")) {
          const planned = parseFloat(values.plannedHours);
          if (isNaN(planned) || planned <= 0) {
            errors.plannedHours = "Плановые часы обязательны (число > 0)";
          }
          if (!values.deadline) errors.deadline = "Срок исполнения обязателен";
        }

        if (project) {
          if (values.start && project.start && values.start < project.start) {
            errors.start = `Раньше начала проекта (${project.start})`;
          }
          if (values.deadline && project.end && values.deadline > project.end) {
            errors.deadline = `Позже окончания проекта (${project.end})`;
          }
        }

        if (values.start && values.deadline && values.deadline < values.start) {
          errors.deadline = "Срок не может быть раньше даты начала";
        } else if (values.deadline && values.deadline < TODAY) {
          errors.deadline = "Срок не может быть в прошлом";
        }

        if (values.parentTaskId) {
          const parent = db.tasks.find((t) => t.id === values.parentTaskId);
          if (parent) {
            if (values.start && parent.start && values.start < parent.start) {
              errors.start = `Раньше начала родителя (${parent.start})`;
            }
            if (
              values.deadline &&
              parent.deadline &&
              values.deadline > parent.deadline
            ) {
              errors.deadline = `Позже срока родителя (${parent.deadline})`;
            }
          }
        }

        if (values.isHourly) {
          if (!values.startTime) errors.startTime = "Укажите время начала";
          if (!values.endTime) errors.endTime = "Укажите время окончания";
          if (
            values.startTime &&
            values.endTime &&
            hoursBetween(values.startTime, values.endTime) === null
          ) {
            errors.endTime = "Время окончания должно быть позже времени начала";
          }
        }

        const tagParse = parseTagsInput(values.tagsInput);
        if (tagParse.invalid.length > 0) {
          errors.tagsInput = `Недопустимые метки: ${tagParse.invalid.join(", ")}`;
        }

        if (!values.priority) errors.priority = "Приоритет обязателен";
        if (!values.status) errors.status = "Статус обязателен";
        return errors;
      },
      [db, isProjectLocked],
    ),
    { fields: FORM_FIELDS },
  );

  const lastSyncedIdRef = useRef(existing?.id);

  useEffect(() => {
    if (existing && existing.id !== lastSyncedIdRef.current) {
      lastSyncedIdRef.current = existing.id;

      setValues(
        buildInitialValues({
          existing,
          isCopy: false,
          copySource: null,
          effectiveProjectId,
          parentTaskId,
          parentTask,
          ur,
        }),
      );

      setTouched({});
    }
  }, [
    existing,
    effectiveProjectId,
    parentTaskId,
    parentTask,
    ur,
    setValues,
    setTouched,
  ]);

  const updateValues = useCallback(
    (patch) => {
      setValues((prev) => ({ ...prev, ...patch }));
      setTouched((prev) => {
        const next = { ...prev };
        Object.keys(patch).forEach((key) => {
          next[key] = true;
        });
        return next;
      });
    },
    [setValues, setTouched],
  );

  const setFieldValueRef = useRef(setFieldValue);
  useEffect(() => {
    setFieldValueRef.current = setFieldValue;
  }, [setFieldValue]);

  return {
    values,
    handleChange,
    updateValues,
    handleSubmit,
    errors,
    touched,
    setValues,
    setTouched,
    setFieldValue,
    isValid,
    isDirty,
    lockedField: null,
  };
}
